import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { alpha, darken } from '@mui/material/styles';
import { ArrowDown, ArrowUp, Car, Link2, Plus, Trash2, X } from 'lucide-react';

import TaskSelectionDialog from '../TaskSelectionDialog';
import { LoadingIndicator } from '../assets';
import GooglePlacePicker from './GooglePlacePicker';
import {
  createEventTaskLink,
  createEventTravelTask,
  deleteEventTaskLink,
  fetchAllTasks,
  getEventTaskLinks,
  updateEventTaskLink,
} from '../../utils/api';

const actionSx = (colorKey = 'primary') => (theme) => {
  const color = theme.palette[colorKey].main;
  return {
    m: 0,
    color,
    borderColor: color,
    boxShadow: `0 4px 0 ${darken(color, 0.58)}`,
    '&:hover': { color, borderColor: color, bgcolor: alpha(color, 0.1) },
    '&:active': { transform: 'translateY(3px)', boxShadow: 'none' },
  };
};

export default function EventTaskLinksPanel({ event, onLinksChange, initialTravelOpen = false }) {
  const [links, setLinks] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [travelOpen, setTravelOpen] = useState(initialTravelOpen);
  const [travelOrigin, setTravelOrigin] = useState(null);
  const [travelMode, setTravelMode] = useState('DRIVE');
  const [travelSuccess, setTravelSuccess] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const onLinksChangeRef = useRef(onLinksChange);

  useEffect(() => {
    onLinksChangeRef.current = onLinksChange;
  }, [onLinksChange]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [loadedLinks, taskResponse] = await Promise.all([
        getEventTaskLinks({ eventId: event.id }),
        fetchAllTasks({ assignment: 'all' }),
      ]);
      setLinks(loadedLinks);
      onLinksChangeRef.current?.(event.id, loadedLinks);
      setTasks(taskResponse.tasks || []);
    } catch (loadError) {
      setError(loadError.data?.message || loadError.message || 'Unable to load preparation tasks.');
    } finally {
      setLoading(false);
    }
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const linkedTaskIds = useMemo(() => new Set(links.map((link) => link.task_id)), [links]);

  const addTask = async (taskId) => {
    if (linkedTaskIds.has(taskId)) {
      setError('That task is already linked to this event.');
      setPickerOpen(false);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await createEventTaskLink({
        event_id: event.id,
        task_id: taskId,
        position: links.length,
      });
      setPickerOpen(false);
      await load();
    } catch (saveError) {
      setError(saveError.data?.message || saveError.message || 'Unable to link that task.');
    } finally {
      setSaving(false);
    }
  };

  const move = async (link, position) => {
    setSaving(true);
    setError('');
    try {
      await updateEventTaskLink(link.id, { position }, link.revision);
      await load();
    } catch (moveError) {
      setError(moveError.data?.message || moveError.message || 'Unable to reorder that task.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (link) => {
    setSaving(true);
    setError('');
    try {
      await deleteEventTaskLink(link.id, link.revision);
      await load();
    } catch (removeError) {
      setError(removeError.data?.message || removeError.message || 'Unable to unlink that task.');
    } finally {
      setSaving(false);
    }
  };

  const addTravel = async () => {
    const destination = event.location || {};
    const hasDestination = Boolean(
      destination.place_id
      || (destination.latitude != null && destination.longitude != null)
    );
    if (!hasDestination) {
      setError("Choose this event's destination with Google Maps before adding travel.");
      return;
    }
    if (!travelOrigin?.place_id && (
      travelOrigin?.latitude == null || travelOrigin?.longitude == null
    )) {
      setError('Choose your starting location with Google Maps.');
      return;
    }

    setSaving(true);
    setError('');
    setTravelSuccess('');
    try {
      const result = await createEventTravelTask(event.id, {
        origin: travelOrigin.place_id
          ? { place_id: travelOrigin.place_id }
          : {
            latitude: travelOrigin.latitude,
            longitude: travelOrigin.longitude,
          },
        travel_mode: travelMode,
      });
      setTravelOrigin(null);
      setTravelOpen(false);
      setTravelSuccess(`Travel task added (${Math.ceil(result.duration_seconds / 60)} minutes).`);
      await load();
    } catch (travelError) {
      setError(
        travelError.data?.message
        || travelError.message
        || 'Unable to calculate travel time.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={(theme) => ({
        p: { xs: 2, sm: 2.5 },
        border: `2px solid ${theme.palette.orange.main}`,
        borderRadius: 4,
        bgcolor: theme.palette.background.paper,
        boxShadow: `0 0 18px ${alpha(theme.palette.orange.main, 0.16)}`,
      })}
    >
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Link2 size={20} />
            <Typography variant="h6" sx={{ color: 'primary.main' }}>Preparation tasks</Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Car size={17} />}
              disabled={saving}
              onClick={() => {
                setTravelOpen((current) => !current);
                setTravelSuccess('');
              }}
              sx={actionSx('orange')}
            >
              Add travel
            </Button>
            <Button
              variant="outlined"
              startIcon={<Plus size={17} />}
              disabled={saving}
              onClick={() => setPickerOpen(true)}
              sx={actionSx('primary')}
            >
              Add task
            </Button>
          </Stack>
        </Stack>

        <Typography variant="caption" sx={{ color: 'secondary.main', textAlign: 'left' }}>
          Only you can see these links. Selecting a parent includes its current bottom-level tasks.
          Preparation time uses each task's saved duration automatically.
        </Typography>
        {error && <Alert severity="error" sx={{ color: 'secondary.main' }}>{error}</Alert>}
        {travelSuccess && <Alert severity="success">{travelSuccess}</Alert>}
        {travelOpen && (
          <Stack
            spacing={1.5}
            sx={(theme) => ({
              p: 2,
              border: `2px solid ${theme.palette.orange.main}`,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.orange.main, 0.05),
            })}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" sx={{ color: 'primary.main' }}>Add travel task</Typography>
              <IconButton
                aria-label="Close travel"
                disabled={saving}
                onClick={() => setTravelOpen(false)}
                sx={{ color: 'secondary.main' }}
              >
                <X size={19} />
              </IconButton>
            </Stack>
            <Typography variant="caption" sx={{ color: 'secondary.main', textAlign: 'left' }}>
              Google calculates the duration once. Your starting location and mode are not saved.
            </Typography>
            <GooglePlacePicker
              label="Starting location"
              value={travelOrigin}
              onChange={setTravelOrigin}
              disabled={saving}
              allowCurrentLocation
            />
            <TextField
              select
              label="Travel mode"
              value={travelMode}
              onChange={(inputEvent) => setTravelMode(inputEvent.target.value)}
              SelectProps={{ native: true }}
              disabled={saving}
            >
              <option value="DRIVE">Drive</option>
              <option value="TRANSIT">Public transit</option>
              <option value="WALK">Walk</option>
              <option value="BICYCLE">Bicycle</option>
              <option value="TWO_WHEELER">Motorcycle or scooter</option>
            </TextField>
            {['WALK', 'BICYCLE', 'TWO_WHEELER'].includes(travelMode) && (
              <Alert severity="warning" sx={{ color: 'orange.main' }}>
                Google says this route may not include clear pedestrian, bicycle, or two-wheel paths.
              </Alert>
            )}
            <Button
              variant="contained"
              startIcon={<Car size={17} />}
              disabled={saving}
              onClick={addTravel}
              sx={actionSx('green')}
            >
              {saving ? 'Calculating...' : 'Create travel task'}
            </Button>
          </Stack>
        )}
        {loading ? <LoadingIndicator text="loading preparation tasks" /> : links.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'secondary.main', textAlign: 'left' }}>
            No preparation tasks linked yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {links.map((link, index) => (
              <Box
                key={link.id}
                sx={(theme) => ({
                  p: 1.5,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.45)}`,
                  borderRadius: 3,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                })}
              >
                <Stack spacing={1.25}>
                  <Stack direction="row" alignItems="flex-start" spacing={1}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: 'primary.main', textAlign: 'left' }}>
                        {(link.task?.full_title || [link.task?.title || 'Unavailable task']).join(' › ')}
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'secondary.main', textAlign: 'left', display: 'block' }}>
                        {link.resolved_leaf_tasks.length} bottom-level {link.resolved_leaf_tasks.length === 1 ? 'task' : 'tasks'}
                      </Typography>
                    </Box>
                    <IconButton
                      aria-label={`Move ${link.task?.title || 'task'} up`}
                      disabled={saving || index === 0}
                      onClick={() => move(link, index - 1)}
                      sx={{ color: 'primary.main' }}
                    >
                      <ArrowUp size={18} />
                    </IconButton>
                    <IconButton
                      aria-label={`Move ${link.task?.title || 'task'} down`}
                      disabled={saving || index === links.length - 1}
                      onClick={() => move(link, index + 1)}
                      sx={{ color: 'primary.main' }}
                    >
                      <ArrowDown size={18} />
                    </IconButton>
                    <IconButton
                      aria-label={`Unlink ${link.task?.title || 'task'}`}
                      disabled={saving}
                      onClick={() => remove(link)}
                      sx={{ color: 'secondary.main' }}
                    >
                      <Trash2 size={18} />
                    </IconButton>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Stack>

      <TaskSelectionDialog
        idPrefix="event-task-link"
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onConfirm={addTask}
        tasks={tasks}
        title="Choose a preparation task"
        confirmLabel="Link task"
        error={error || null}
        isLoading={loading}
        isSubmitting={saving}
        submittingText="linking task"
      />
    </Box>
  );
}
